import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import TextInput from '../../../shared/Inputs/TextInput/TextInput'
import DatePicker from '../../../shared/Inputs/DatePicker/DatePicker'
import { yearsAgo } from '../../../utils/helpers'
import {
  validateDate,
  validateString,
  smartValidateString,
} from '../../../validate/validators'
import { Country, Lang } from '../../../types/common-types'
import { State } from '../../../state-actions-reducers'
import SelectInput from '../../../shared/Inputs/SelectInput/SelectInput'
import styles from '../ScreenOne.module.scss'
import RussianForm from './Citizenships/RussianForm'
import KyrgyzstanForm from './Citizenships/KyrgyzstanForm'
import RomanianForm from './Citizenships/RomanianForm'
import ChinaForm from './Citizenships/ChinaForm'
import KazakhstanForm from './Citizenships/KazakhstanForm'
import BelarusForm from './Citizenships/BelarusForm'
import UkraineForm from './Citizenships/UkraineForm'
import UzbekistanForm from './Citizenships/UzbekistanForm'
import VietnamForm from './Citizenships/VietnamForm'
import GermanyForm from './Citizenships/GermanyForm'
import UsaForm from './Citizenships/UsaForm'
import TajikistanForm from './Citizenships/TajikistanForm'
import TurkishForm from './Citizenships/TurkishForm'
import NigeriaForm from './Citizenships/NigeriaForm'
import LitvaForm from './Citizenships/LitvaForm'
import IndiaForm from './Citizenships/IndiaForm'
import SwedenForm from './Citizenships/SwedenForm'
import AddressInputs from '../AddressInputs/AddressInputs'
import AdditionalDocuments from '../AdditionalDocuments/AdditionalDocuments'
import { SexSelect } from '../../../shared/Inputs/SexSelect/SexSelect'
import {
  passportTypeKz,
  passportTypeRfForKz,
  passportTypeRom,
  passportTypeKgz,
  passportTypeGbr,
  passportTypeBel,
  passportTypeUkr,
  passportTypeLitva,
  passportTypeGer,
  passportTypeUsa,
  passportTypeTaj,
  passportTypeTur,
  passportTypeUzb,
  DocumentTypeValuesUdvAbroad,
  DocumentTypeValuesTypePassport,
  passportTypeIndia,
  passportTypeSwe,
  passportTypeSKorea,
  passportTypeNga,
  passportTypeIrn,
  passportTypeChn,
  passportTypeAus,
  passportTypePakistan,
  passportTypeBrunei,
} from '../../../content/passportType'
import {
  additionalDocumentTypeValuesKz,
  additionalDocumentTypeValuesRfForKz,
  additionalDocumentTypeValuesKgz,
  additionalDocumentTypeValuesOthers,
  additionalDocumentTypeValuesGbr,
} from '../../../content/additionalDocumentType'
import { kzCity } from '../../../content/cities'
import AddCitizenships from '../../../components/AddCitizenships/AddCitizenships'
import BritishForm from './Citizenships/BritishForm'
import { familyStatusMaleOptions, familyStatusFemaleOptions } from '../../../content/familyStatusType'
import SouthKoreaForm from './Citizenships/SouthKoreaForm'
import IranForm from './Citizenships/IranForm'
import AustraliaForm from './Citizenships/AustraliaForm'
import PakistanForm from './Citizenships/PakistanForm'
import BruneiForm from './Citizenships/BruneiForm'
import useTransliterate from '../../../utils/useTransliterate'

interface Props {
  lang: Lang
  accountForm: any
  citizenshipCountriesList: Country[]
  allCitizenships: Country[]
  addressCountries: Country[]
  regionCityRegistered: any
  regionCityFactual: any
}

const CITIZENSHIP_PASSPORT_TYPE_MAP: Record<string, any> = {
  'Казахстан': passportTypeKz,
  'Российская Федерация': passportTypeRfForKz,
  'Кыргызстан': passportTypeKgz,
  'Великобритания': passportTypeGbr,
  'Беларусь': passportTypeBel,
  'Украина': passportTypeUkr,
  'Румыния': passportTypeRom,
  'Германия': passportTypeGer,
  'Соединенные Штаты': passportTypeUsa,
  'Австралия': passportTypeAus,
  'Таджикистан': passportTypeTaj,
  'Турция': passportTypeTur,
  'Иран, Исламская Республика': passportTypeIrn,
  'Нигерия': passportTypeNga,
  'Китай': passportTypeChn,
  'Узбекистан': passportTypeUzb,
  'Литва': passportTypeLitva,
  'Индия': passportTypeIndia,
  'Пакистан': passportTypePakistan,
  'Бруней-Даруссалам': passportTypeBrunei,
  'Швеция': passportTypeSwe,
  'Южная Корея': passportTypeSKorea,
}

type CitizenshipFormProps = { lang: any; accountForm: any }

const CITIZENSHIP_FORM_MAP: Record<string, React.ComponentType<CitizenshipFormProps>> = {
  'Российская Федерация': RussianForm,
  'Беларусь': BelarusForm,
  'Бразилия': BelarusForm,
  'Италия': BelarusForm,
  'Украина': UkraineForm,
  'Кыргызстан': KyrgyzstanForm,
  'Великобритания': BritishForm,
  'Румыния': RomanianForm,
  'Китай': ChinaForm,
  'Казахстан': KazakhstanForm,
  'Германия': GermanyForm,
  'Соединенные Штаты': UsaForm,
  'Австралия': AustraliaForm,
  'Таджикистан': TajikistanForm,
  'Турция': TurkishForm,
  'Иран, Исламская Республика': IranForm,
  'Пакистан': PakistanForm,
  'Бруней-Даруссалам': BruneiForm,
  'Нигерия': NigeriaForm,
  'Литва': LitvaForm,
  'Индия': IndiaForm,
  'Швеция': SwedenForm,
  'Южная Корея': SouthKoreaForm,
  'Азербайджан': UzbekistanForm,
  'Армения': UzbekistanForm,
  'Соединенное Королевство': UzbekistanForm,
  'Испания': UzbekistanForm,
  'Сирийская Арабская Республика': UzbekistanForm,
  'Узбекистан': UzbekistanForm,
  'Франция': UzbekistanForm,
  'Швейцария': UzbekistanForm,
  'Болгария': VietnamForm,
  'Израиль': VietnamForm,
  'Исландия': VietnamForm,
  'Молдова, Республика': VietnamForm,
  'Нидерланды': VietnamForm,
  'Норвегия': VietnamForm,
  'Южная Африка': VietnamForm,
  'Вьетнам': VietnamForm,
  'Грузия': VietnamForm,
  'Индонезия': VietnamForm,
  'Финляндия': VietnamForm,
}

/* REACT COMPONENT */
function ScreenOne({
  lang,
  accountForm,
  citizenshipCountriesList,
  allCitizenships,
  addressCountries,
  regionCityRegistered,
  regionCityFactual,
}: Props) {
  const { watch, setValue, formState: { errors } } = useFormContext<State>()
  const state = watch()

  const countriesRef = useRef(
    allCitizenships?.reduce(
      (object, country) => ({
        ...object,
        [country.alpha2 as any]: (country as any)[lang],
      }),
      {}
    )
  )
  const countries = countriesRef.current
  const [focus, setFocus] = useState('')
  const getCitizenshipFormat = (citizenship: string) => {
    return citizenshipCountriesList?.find((country: Country) => {
      return country.value === citizenship
    })?.label?.rus
  }
  const selectedCitizenship = useMemo(() => {
    return getCitizenshipFormat(state.citizenship)
  }, [state.citizenship])

  useEffect(() => {
    setValue('mainCity', kzCity[0].value, { shouldDirty: true })
  }, [lang])

  useEffect(() => {
    if (state.passportType?.length === 0) {
      const passportOptions = selectedCitizenship ? CITIZENSHIP_PASSPORT_TYPE_MAP[selectedCitizenship] : null
      setValue('passportType',
        passportOptions
          ? String(passportOptions[0].value)
          : String(DocumentTypeValuesTypePassport[0].value),
        { shouldDirty: true }
      )
    }
  }, [selectedCitizenship])
  const [showAddCitizenships, setShowAddCitizenships] = useState(false)

  const CitizenshipForm = selectedCitizenship ? CITIZENSHIP_FORM_MAP[selectedCitizenship] ?? null : null
  const commonProps = { lang, accountForm }

  return (
    <>
      <main className={styles.main}>
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3>{accountForm.stepOnePersonalTitle[lang]}</h3>
            </div>
            {lang !== 'eng' ? (
              <>
                <div className="col-4 col-xs-12">
                  {/* LAST NAME */}
                  <TextInput
                    testName="lastname"
                    testErrorName="error-lastname"
                    disabled={!state.agreed}
                    lang={lang}
                    content={accountForm.lastName}
                    onChange={lastName => {
                        setValue('lastName', lastName, { shouldDirty: true })
                        if (lang === 'rus' || lang === 'kz') setValue('latinLastname', useTransliterate(lastName), { shouldDirty: true })
                      }
                    }
                    value={state.lastName || ''}
                    required
                    valid={
                      state.lastName.length > 0
                        ? smartValidateString(state.lastName) &&
                          !errors.lastName
                        : !errors.lastName
                    }
                  />
                </div>
                <div className="col-4 col-xs-12">
                  {/* FIRST NAME */}
                  <TextInput
                    testName="firstname"
                    testErrorName="error-firstname"
                    disabled={!state.agreed}
                    lang={lang}
                    content={accountForm.firstName}
                    onChange={firstName => {
                      setValue('firstName', firstName, { shouldDirty: true })
                      if (lang === 'rus' || lang === 'kz') setValue('latinFirstname', useTransliterate(firstName), { shouldDirty: true })
                    }}
                    value={state.firstName || ''}
                    required
                    valid={
                      state.firstName.length > 0
                        ? smartValidateString(state.firstName) &&
                          !errors.firstName
                        : !errors.firstName
                    }
                  />
                </div>
                <div className="col-4 col-xs-12">
                  {/* PATRONYMIC */}
                  <TextInput
                    testName="patronymic"
                    testErrorName="error-patronymic"
                    testCheckName={'noPatronymic'}
                    disabled={!state.agreed || state.noPatronymic}
                    lang={lang}
                    content={accountForm.patronymic}
                    onChange={patronymic => {
                      setValue('patronymic', patronymic, { shouldDirty: true })
                      if (lang === 'rus' || lang === 'kz') setValue('latinPatronymic', useTransliterate(patronymic), { shouldDirty: true })
                    }}
                    checkLabel={accountForm.noPatronymic[lang]}
                    checkValue={state.noPatronymic}
                    checkChange={() => {
                      setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true })
                      setValue('patronymic', '', { shouldDirty: true })
                      setValue('latinPatronymic', '', { shouldDirty: true })
                    }}
                    required
                    value={state.patronymic || ''}
                    valid={
                      state.patronymic.length > 0
                        ? smartValidateString(state.patronymic) &&
                          !errors.patronymic
                        : !errors.patronymic
                    }
                  />
                </div>
              </>
            ) : null}
            <div className="col-4 col-xs-12">
              {/* LATIN LAST NAME */}
              <TextInput
                testName="latinLastname"
                testErrorName="error-latinLastname"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.latinLastname}
                onChange={latinLastname => {
                  latinLastname = latinLastname.replace(
                    /[^A-Za-z0-9\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\'\ ]/gi,
                    ''
                  )
                  setValue('latinLastname', latinLastname, { shouldDirty: true })
                  if (lang === 'eng') setValue('lastName', latinLastname, { shouldDirty: true })
                }}
                value={state.latinLastname || ''}
                required
                valid={
                  state.latinLastname.length > 0
                    ? smartValidateString(state.latinLastname) &&
                      !errors.latinLastname
                    : !errors.latinLastname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* LATIN FIRST NAME */}
              <TextInput
                testName="latinFirstname"
                testErrorName="error-latinFirstname"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.latinFirstname}
                onChange={latinFirstname => {
                  latinFirstname = latinFirstname.replace(
                    /[^A-Za-z0-9\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\'\ ]/gi,
                    ''
                  )
                  setValue('latinFirstname', latinFirstname, { shouldDirty: true })
                  if (lang === 'eng') setValue('firstName', latinFirstname, { shouldDirty: true })
                }}
                value={state.latinFirstname || ''}
                required
                valid={
                  state.latinFirstname.length > 0
                    ? smartValidateString(state.latinFirstname) &&
                      !errors.latinFirstname
                    : !errors.latinFirstname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* LATIN PATRONYMIC */}
              <TextInput
                testName="latinPatronymic"
                testErrorName="error-latinPatronymic"
                testCheckName={'noPatronymic'}
                disabled={!state.agreed || state.noPatronymic}
                lang={lang}
                content={accountForm.latinPatronymic}
                checkLabel={
                  lang === 'eng' ? accountForm.noPatronymic[lang] : undefined
                }
                checkValue={lang === 'eng' ? state.noPatronymic : false}
                checkChange={() => {
                  if (lang === 'eng') {
                    setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true })
                    setValue('latinPatronymic', '', { shouldDirty: true })
                    setValue('patronymic', '', { shouldDirty: true })
                  }
                }}
                onChange={latinPatronymic => {
                  latinPatronymic = latinPatronymic.replace(
                    /[^A-Za-z0-9\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\'\ ]/gi,
                    ''
                  )
                  setValue('latinPatronymic', latinPatronymic, { shouldDirty: true })
                  if (lang === 'eng') setValue('patronymic', latinPatronymic, { shouldDirty: true })
                }}
                required
                value={state.latinPatronymic || ''}
                valid={
                  state.latinPatronymic.length > 0
                    ? smartValidateString(state.latinPatronymic) &&
                      !errors.latinPatronymic
                    : !errors.latinPatronymic
                }
              />
            </div>
          </div>
          <div className="row">
            <div className="col-4 col-xs-12">
              {/* CITIZENSHIP */}
              <SelectInput
                testName={'citizenship'}
                testErrorName={'error-citizenship'}
                noOptionsMessage={accountForm.citizenshipInput.placeholder}
                lang={lang}
                disabled={!state.agreed}
                content={accountForm.citizenshipInput}
                options={citizenshipCountriesList?.filter((e) => !e.disabled)}
                onChange={citizenship => {
                  setValue('citizenship', citizenship, { shouldDirty: true })
                  const fmt = getCitizenshipFormat(citizenship)
                  const passportOptions = fmt ? CITIZENSHIP_PASSPORT_TYPE_MAP[fmt] : null
                  setValue('passportType',
                    passportOptions
                      ? String(passportOptions[0].value)
                      : String(DocumentTypeValuesTypePassport[0].value),
                    { shouldDirty: true }
                  )
                }}
                valid={!errors.citizenship}
                value={state.citizenship}
                required
                prefix="Citizenships"
              />
              <span
                className={styles.addCitizenships}
                onClick={() => setShowAddCitizenships(!showAddCitizenships)}
              >
                {accountForm.noCitizenshipsLink[lang]}
              </span>
            </div>
            <div className="col-3 col-xs-12">
              {/* BIRTH DATE */}
              <DatePicker
                testName="birthDate"
                testErrorName="error-birthDate"
                disabled={!state.agreed}
                lang={lang}
                accountForm={accountForm}
                accountFormKey="birthDate"
                onChange={birthDate =>
                  setValue('birthDate', birthDate, { shouldDirty: true })
                }
                value={state.birthDate}
                valid={
                  !errors.birthDate &&
                  validateDate({
                    current: state.birthDate,
                    min: yearsAgo(130),
                    max: yearsAgo(14),
                  })
                }
                curr={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18)
                  )
                }
                min={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 85)
                  )
                }
                max={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18)
                  )
                }
                required
              />
            </div>
            <div className="col-5 col-xs-12">
              {/* BIRTH PLACE */}
              <TextInput
                testName="birthPlace"
                testErrorName="error-birthPlace"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.birthPlace}
                onChange={birthPlace => {
                  setValue('birthPlace', birthPlace, { shouldDirty: true })
                }}
                value={state.birthPlace}
                required
                valid={
                  state.birthPlace.length > 0
                    ? smartValidateString(state.birthPlace) &&
                      !errors.birthPlace
                    : !errors.birthPlace
                }
                onBlur={() => setFocus('')}
              />
            </div>
            {showAddCitizenships ? (
              <div className="col-12">
                <AddCitizenships
                  accountForm={accountForm}
                  lang={lang}
                  noContacts={true}
                  handlerChange={() => setShowAddCitizenships(false)}
                />
              </div>
            ) : null}
            <SexSelect
              lang={lang}
              accountForm={accountForm}
            />
            {/* FAMILY_STATUS */}
            <div className="col-4 col-xs-12">
              <SelectInput
                lang={lang}
                noOptionsMessage={accountForm.familyStatus.placeholder}
                content={accountForm.familyStatus}
                value={state.familyStatus}
                options={state.sex === '2' ? familyStatusMaleOptions : familyStatusFemaleOptions}
                valid={
                  !errors.familyStatus
                }
                onChange={data =>
                  setValue('familyStatus', data, { shouldDirty: true })
                }
                disabled={state.sex === '0'}
                className={styles.input}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-4 col-xs-12">
              <SelectInput
                testName={'passportType'}
                testErrorName={'error-passportType'}
                lang={lang}
                noOptionsMessage={accountForm.passportTypeInput.placeholder}
                options={
                  selectedCitizenship
                    ? (CITIZENSHIP_PASSPORT_TYPE_MAP[selectedCitizenship] ?? DocumentTypeValuesTypePassport)
                    : DocumentTypeValuesTypePassport
                }
                content={accountForm.passportTypeInput}
                onChange={data => {
                  setValue('passportType', data, { shouldDirty: true })
                }}
                value={state.passportType}
                valid={
                  !errors.passportType &&
                  validateString(state.passportType, 300, true)
                }
                required
              />
            </div>
            {CitizenshipForm && <CitizenshipForm {...commonProps} />}
          </div>
        </div>
        <AddressInputs
          lang={lang}
          accountForm={accountForm}
          addressCountries={addressCountries}
          regionCityRegistered={regionCityRegistered}
          regionCityFactual={regionCityFactual}
        />
        <AdditionalDocuments
          lang={lang}
          accountForm={accountForm}
          selectedCitizenship={selectedCitizenship}
          documentsTypeValues={
            selectedCitizenship === 'Казахстан'
                ? additionalDocumentTypeValuesKz
                : selectedCitizenship === 'Российская Федерация'
                ? additionalDocumentTypeValuesRfForKz
                : selectedCitizenship === 'Кыргызстан'
                ? additionalDocumentTypeValuesKgz
                : selectedCitizenship === 'Великобритания'
                ? additionalDocumentTypeValuesGbr
                : additionalDocumentTypeValuesOthers
          }
        />
      </main>
    </>
  )
}

export default ScreenOne
